// BUILD-046 task 8.10: the origin label on a task row or card (Req 1.10, 13.19). The rule lives in originModel.ts.
//
// Correction cycle 1bb88b3e (stage B2a, item N9): quiet, compact SECONDARY metadata ("From Pricing"), not a boxed badge competing with the title.
// The words are the distinction (never colour alone); a SIMULATED marker stays on a source-owned task whose adapter is simulated.
//
// Session edit 4 (requester badge): a MANUAL task no longer shows the word "Manual" on the row. That fact moved into the requester badge's tooltip ("Manual task",
// requesterBadgeModel.ts), so the row is not cluttered; a source-linked task keeps its quiet visible "From <module>" (and SIMULATED), and so does a task with a
// restricted source. `hasVisibleOrigin` tells a row whether there is a chip to draw, so it does not leave an empty part (and a stray separator) in its quiet line.
import { originLabel } from './originModel';
import type { OriginInput } from './originModel';

export interface OriginChipProps {
  /** The task (only `sourceRef`, `sourceGoverned` and `redacted` are read). */
  item: OriginInput;
  /** True when the source adapter is simulated (in the pilot, whenever `GET /work/me` reports `simulated`). */
  adapterSimulated?: boolean;
}

/** True when the task's origin is drawn on the row: any task that is not manual. */
export function hasVisibleOrigin(item: OriginInput): boolean {
  return originLabel(item).kind !== 'manual';
}

export function OriginChip({ item, adapterSimulated = false }: OriginChipProps) {
  const label = originLabel(item, { adapterSimulated });
  if (label.kind === 'manual') return null;
  // Layout-11: quiet but readable - indigo-700 on white is 8:1 (gray-400 was 2.5:1).
  return (
    <span className="inline-flex items-center gap-1" data-testid="origin-chip">
      <span className="text-xs text-indigo-700">{label.text}</span>
      {label.simulated && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">SIMULATED</span>}
    </span>
  );
}
